import {
  type ReactNode,
  useRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  isValidElement,
  cloneElement,
  Children,
  type RefObject,
  type ReactElement,
} from 'react';

import { Stack } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';

import styles from './Panels.module.scss';
import { PanelContent, type PanelContentProps } from '../PanelContent';
import { IconButton } from '@/components/ui/IconButton';

export type DrawerPanelHandle = {
  focus: () => void;
};

interface SharedPanelProps {
  children: ReactNode;
  className?: string;
  panelContentProps?: Omit<PanelContentProps, 'children'>;
}

interface DrawerPanelProps extends SharedPanelProps {
  minSize?: number;
  collapsedSize?: number;
}

interface InjectedLayoutProps {
  defaultSize?: number;
  panelRef?: RefObject<ImperativePanelHandle | null>;
}

interface InjectedPanelProps {
  panelRef?: RefObject<ImperativePanelHandle | null>;
  panelIsCollapsed?: boolean;
  setPanelIsCollapsed?: (collapsed: boolean) => void;
  collapsePanel?: () => void;
  expandPanel?: () => void;
  drawerContentRef?: RefObject<HTMLDivElement | null>;
}

interface PanelsProps {
  children: ReactNode;
  autoSaveId: string;
  direction?: 'horizontal' | 'vertical';
  defaultLayoutRatio?: '2:1' | '1:1';
  className?: string;
  expandDrawer?: boolean;
  onDrawerCollapse?: () => void;
  collapseDrawerOnEscape?: boolean;
  drawerRef?: RefObject<DrawerPanelHandle | null>;
  onEscapeFromDrawer?: () => void;
}

// Specific panel components with fixed behavior
const MainPanelComponent = ({
  children,
  className,
  panelContentProps = {
    hideOverflow: false,
    hasPaddingBottom: true,
  },
  ...InjectedLayoutProps
}: SharedPanelProps & InjectedLayoutProps) => {
  const { defaultSize, panelRef } = InjectedLayoutProps;

  return (
    <Panel
      ref={panelRef}
      minSize={40}
      className={className}
      defaultSize={defaultSize}
    >
      <PanelContent {...panelContentProps}>{children}</PanelContent>
    </Panel>
  );
};

const SecondaryPanelComponent = ({
  children,
  className,
  panelContentProps = {
    hideOverflow: false,
    hasPaddingBottom: true,
  },
}: SharedPanelProps) => {
  return (
    <Panel minSize={20} className={className}>
      <PanelContent {...panelContentProps}>{children}</PanelContent>
    </Panel>
  );
};

const DrawerPanelComponent = forwardRef<
  DrawerPanelHandle,
  DrawerPanelProps & InjectedPanelProps
>(
  (
    {
      children,
      className,
      minSize = 15,
      collapsedSize = 3,
      panelContentProps = {
        hasPaddingBottom: true,
      },
      // These props will be injected by the parent
      ...injectedProps
    },
    ref,
  ) => {
    const {
      panelRef,
      panelIsCollapsed,
      setPanelIsCollapsed,
      collapsePanel,
      expandPanel,
      drawerContentRef,
    } = injectedProps;

    // Expose focus API
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          const content = drawerContentRef?.current;
          if (!content) return;

          // Don't focus if the drawer is collapsed or not visible
          if (panelIsCollapsed) return;

          // Verify element is actually visible in the DOM
          if (content.offsetParent === null) return;

          // Scroll to top for consistent UX
          content.scrollTop = 0;

          // Focus the panel content
          content.focus();
        },
      }),
      [panelIsCollapsed, drawerContentRef],
    );

    return (
      <Panel
        ref={panelRef}
        collapsible
        collapsedSize={collapsedSize}
        minSize={minSize}
        className={cx(className, styles.Drawer, {
          [styles.PanelIsCollapsed]: panelIsCollapsed,
        })}
        onCollapse={() => setPanelIsCollapsed?.(true)}
        onExpand={() => setPanelIsCollapsed?.(false)}
      >
        <PanelContent
          ref={drawerContentRef}
          {...panelContentProps}
          hideOverflow={panelIsCollapsed || panelContentProps?.hideOverflow}
        >
          <Stack
            direction="row"
            justifyContent={panelIsCollapsed ? 'center' : 'end'}
            className={styles.CollapseExpandButton}
          >
            <IconButton
              iconName={
                panelIsCollapsed
                  ? 'keyboard_arrow_left'
                  : 'keyboard_arrow_right'
              }
              tooltip={panelIsCollapsed ? 'Expand' : 'Collapse'}
              onClick={panelIsCollapsed ? expandPanel : collapsePanel}
              size="small"
            />
          </Stack>
          {!panelIsCollapsed ? children : null}
        </PanelContent>
      </Panel>
    );
  },
);

// Add display names to identify components
DrawerPanelComponent.displayName = 'DrawerPanel';
MainPanelComponent.displayName = 'MainPanel';

export const Panels = ({
  children,
  direction = 'horizontal',
  defaultLayoutRatio = '2:1',
  autoSaveId,
  className,
  expandDrawer,
  onDrawerCollapse,
  collapseDrawerOnEscape = false,
  drawerRef,
  onEscapeFromDrawer,
}: PanelsProps) => {
  const { t } = useTranslation('ui');
  const [panelIsCollapsed, setPanelIsCollapsed] = useState(false);
  const panelRef = useRef<ImperativePanelHandle>(null);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  const collapsePanel = useCallback(
    (options?: { notify?: boolean }) => {
      const panel = panelRef.current;
      if (!panel) return;

      panel.collapse();
      setPanelIsCollapsed(true);
      if (options?.notify !== false) {
        onDrawerCollapse?.();
      }
    },
    [onDrawerCollapse],
  );

  const expandPanel = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;

    panel.expand();
    setPanelIsCollapsed(false);
  }, []);

  // Keep drawer in sync when controlled by expandDrawer
  useEffect(() => {
    if (expandDrawer === undefined) return;
    if (expandDrawer) {
      expandPanel();
    } else {
      collapsePanel({ notify: false });
    }
  }, [expandDrawer, expandPanel, collapsePanel]);

  // Add ESC key listener to collapse drawer when expanded
  useEffect(() => {
    if (!collapseDrawerOnEscape) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || panelIsCollapsed) return;

      // Check if the drawer content currently has focus
      const drawerHasFocus =
        drawerContentRef.current &&
        drawerContentRef.current.contains(document.activeElement);

      // If drawer had focus, we need to return focus after closing
      if (drawerHasFocus) {
        event.preventDefault(); // Prevent default Escape behavior

        collapsePanel();

        // Wait for collapse animation and then delegate focus restoration to parent
        // Double RAF ensures focus happens after: (1) React render + DOM update, (2) panel expansion animation starts
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            onEscapeFromDrawer?.();
          });
        });
      } else {
        // Drawer doesn't have focus, just collapse normally
        collapsePanel();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [
    collapseDrawerOnEscape,
    panelIsCollapsed,
    collapsePanel,
    onEscapeFromDrawer,
  ]);

  // Store ref for main panel to enable resetting
  const mainPanelRef = useRef<ImperativePanelHandle>(null);

  // Calculate default sizes based on defaultLayoutRatio
  const getDefaultSizes = useCallback(() => {
    if (!defaultLayoutRatio) return null;
    const [left, right] = defaultLayoutRatio.split(':').map(Number);
    const mainSize = (left / (left + right)) * 100;
    return { mainSize };
  }, [defaultLayoutRatio]);

  const handleResizeHandleDoubleClick = useCallback(() => {
    const defaultSizes = getDefaultSizes();
    if (!defaultSizes) return;

    // Reset main panel to default size
    mainPanelRef.current?.resize(defaultSizes.mainSize);
  }, [getDefaultSizes]);

  // Inject props into collapsible panels and add consistent resize handles
  const enhancedChildren: ReactNode[] = [];
  let hasDrawer = false;

  Children.forEach(children, (child, index) => {
    if (isValidElement(child)) {
      let processedChild = child;

      // Handle DrawerPanel (always collapsible)
      if (
        child.type === DrawerPanelComponent ||
        (child.type as { displayName?: string })?.displayName === 'DrawerPanel'
      ) {
        hasDrawer = true;
        processedChild = cloneElement(
          child as ReactElement<DrawerPanelProps & InjectedPanelProps>,
          {
            panelRef,
            panelIsCollapsed,
            setPanelIsCollapsed,
            collapsePanel,
            expandPanel,
            drawerContentRef,
            ref: drawerRef,
            // TypeScript doesn't support ref in cloneElement props, need type assertion
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any,
        );
      }
      // add default size based on defaultLayoutRatio for MainPanel
      if (
        child.type === MainPanelComponent ||
        (child.type as { displayName?: string })?.displayName === 'MainPanel'
      ) {
        const defaultSizes = getDefaultSizes();
        const mainPanelDefaultSize = defaultSizes?.mainSize;

        processedChild = cloneElement(
          child as ReactElement<SharedPanelProps & InjectedLayoutProps>,
          {
            defaultSize: mainPanelDefaultSize,
            panelRef: mainPanelRef,
          },
        );
      }

      // Add the processed child with a key
      enhancedChildren.push(
        cloneElement(processedChild as ReactElement, { key: `panel-${index}` }),
      );

      // Add resize handle after each panel except the last one
      if (index < Children.count(children) - 1) {
        enhancedChildren.push(
          <PanelResizeHandle
            key={`handle-${index}`}
            className={styles.ResizeHandle}
            onDoubleClick={handleResizeHandleDoubleClick}
            data-automation-id="panels-resize-handle"
          />,
        );
      }
    } else {
      enhancedChildren.push(
        isValidElement(child)
          ? cloneElement(child as ReactElement, { key: `child-${index}` })
          : child,
      );
    }
  });

  return (
    <PanelGroup
      direction={direction}
      autoSaveId={autoSaveId}
      className={cx(styles.PanelGroup, className)}
    >
      {hasDrawer && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={styles.ScreenReaderStatus}
        >
          {panelIsCollapsed
            ? t('panels.SidePanelIsClosedLabel')
            : t('panels.SidePanelIsOpenedLabel')}
        </div>
      )}
      {enhancedChildren}
    </PanelGroup>
  );
};

// Attach the Panel components to Panels for compound component pattern
Panels.Main = MainPanelComponent; // Fixed main panel
Panels.Secondary = SecondaryPanelComponent; // Fixed secondary panel
Panels.Drawer = DrawerPanelComponent; // Fixed drawer panel
